/**
 * Turning a balance into a decision.
 *
 * "240 credits left" is a number nobody can act on. "240 left — about 8 days
 * at your current rate" is: it says whether to top up now or ignore it. The
 * forecast is deliberately coarse (a week's spend, divided) because it is a
 * prompt, not an invoice, and precision would imply a promise we can't keep.
 */

export interface CreditForecast {
  /** Average credits per day over the last week. */
  perDay: number;
  /** Whole days of runway left at that rate. */
  daysLeft: number;
  /** Runway short enough to be worth saying out loud unprompted. */
  urgent: boolean;
}

/** Below this many days of runway, the forecast is worth surfacing. */
const URGENT_DAYS = 10;

/**
 * Returns null when there is nothing honest to say: no spending to
 * extrapolate from, or no balance to run out of.
 */
export function creditForecast(input: {
  balance: number;
  spent7d: number;
}): CreditForecast | null {
  const { balance, spent7d } = input;
  if (!Number.isFinite(balance) || !Number.isFinite(spent7d)) return null;
  if (spent7d <= 0 || balance <= 0) return null;

  const perDay = spent7d / 7;
  // Floor, not round: promising a tenth day and delivering nine reads as a
  // broken promise, while under-promising never does.
  const daysLeft = Math.max(0, Math.floor(balance / perDay));
  return { perDay, daysLeft, urgent: daysLeft <= URGENT_DAYS };
}
